import { useRef, useState } from 'react';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const MAX_CLIP_DURATION_SECONDS = 60;
export const MIN_CLIP_DURATION_SECONDS = 3;

// Slots shown before frames arrive; matches THUMBNAIL_COUNT in ClipPreviewPlayer.
const FILMSTRIP_SLOTS = 10;

// Presentational + pointer-drag math only — no video.js knowledge.
const ClipTrimmer = ({
  thumbnails,
  windowStart,
  windowEnd,
  clipStart,
  clipEnd,
  currentTime,
  isPlaying,
  onTogglePlay,
  onChange,
}) => {
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  const [dragPlayhead, setDragPlayhead] = useState(null);

  const windowDuration = windowEnd - windowStart;
  const toPercent = (seconds) => `${((seconds - windowStart) / windowDuration) * 100}%`;

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      startClientX: e.clientX,
      startClipStart: clipStart,
      startClipEnd: clipEnd,
      // Offset of the paused playhead into the clip, held constant so it rides the drag.
      playheadOffset: clamp(currentTime, clipStart, clipEnd) - clipStart,
      trackWidth: trackRef.current.getBoundingClientRect().width,
    };
  };

  // Each drag pins the untouched edge and clamps the dragged edge to the window + min/max duration.
  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const deltaSeconds = ((e.clientX - drag.startClientX) / drag.trackWidth) * windowDuration;

    let start;
    let end;
    if (drag.mode === 'start') {
      start = Math.min(drag.startClipStart + deltaSeconds, drag.startClipEnd - MIN_CLIP_DURATION_SECONDS);
      start = Math.max(start, windowStart, drag.startClipEnd - MAX_CLIP_DURATION_SECONDS);
      end = drag.startClipEnd;
    } else if (drag.mode === 'end') {
      end = Math.max(drag.startClipEnd + deltaSeconds, drag.startClipStart + MIN_CLIP_DURATION_SECONDS);
      end = Math.min(end, windowEnd, drag.startClipStart + MAX_CLIP_DURATION_SECONDS);
      start = drag.startClipStart;
    } else {
      const duration = drag.startClipEnd - drag.startClipStart;
      start = Math.min(Math.max(windowStart, drag.startClipStart + deltaSeconds), windowEnd - duration);
      end = start + duration;
    }

    const playhead = clamp(start + drag.playheadOffset, start, end);
    setDragPlayhead(playhead);
    onChange({ start, end, playhead });
  };

  const endDrag = () => {
    dragRef.current = null;
    setDragPlayhead(null);
  };

  const playheadTime = dragPlayhead ?? currentTime;
  const showPlayhead = playheadTime > windowStart && playheadTime < windowEnd;

  return (
    <div className='clip-trimmer d-flex bg-main-blue'>
      <div className='bg-main-white'>
        <button
          type='button'
          className='h-100 w-100 general-button flex-centered justify-content-center clip-trimmer-play-button mr-3 bg-main-blue text-white title-h5-m'
          onClick={onTogglePlay}
        >
          {isPlaying ? (
            <span className='clip-trimmer-pause-icon' />
          ) : (
            <span className='clip-trimmer-play-icon specific-left-margin-2 d-inline-block ' />
          )}
        </button>
      </div>
      <div
        ref={trackRef}
        className='clip-trimmer-track position-relative flex-grow-1 overflow-hidden rounded'
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div className='d-flex h-100'>
          {Array.from({ length: Math.max(FILMSTRIP_SLOTS, thumbnails.length) }).map((_, index) =>
            thumbnails[index] ? (
              <img key={index} src={thumbnails[index]} alt='' className='clip-trimmer-thumbnail' />
            ) : (
              <div key={index} className='clip-trimmer-thumbnail clip-trimmer-thumbnail--empty' />
            )
          )}
        </div>
        <div className='clip-trimmer-dim' style={{ left: 0, width: toPercent(clipStart) }} />
        <div className='clip-trimmer-dim' style={{ left: toPercent(clipEnd), right: 0 }} />
        {showPlayhead && <div className='clip-trimmer-playhead' style={{ left: toPercent(playheadTime) }} />}
        <div
          className='clip-trimmer-selection'
          style={{ left: toPercent(clipStart), width: `${((clipEnd - clipStart) / windowDuration) * 100}%` }}
          onPointerDown={(e) => startDrag(e, 'move')}
        >
          <button
            type='button'
            className='clip-trimmer-handle clip-trimmer-handle-start'
            onPointerDown={(e) => startDrag(e, 'start')}
          >
            <span className='icon-arrow-back' />
          </button>
          <button
            type='button'
            className='clip-trimmer-handle clip-trimmer-handle-end'
            onPointerDown={(e) => startDrag(e, 'end')}
          >
            <span className='icon-arrow-next' />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClipTrimmer;
